import {
  createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel
} from "@tanstack/react-table";
import { type BusinessHierarchyInvitation } from "lens-platform-sdk";
import { action } from "@k8slens/lds-icons";

import Table from "src/components/Table/Table";

import styles from "src/components/Business/PendingChildrenTable.module.css";
import { ContextMenu, ContextMenuItem } from "src/components/ContextMenu/ContextMenu";
import { ContextMenuIcon } from "src/components/Icon/Navigation";
import { useParams } from "react-router-dom";
import { useBusinessChildInvitation } from "src/hooks/useBusinessChildInvitation";
import { dateToString, defaultDateTimeFormatOptions } from "src/utils/date";
import { useMemo } from "react";
import { useTrackedTableSort } from "./useTrackedTableSort";

const { DeleteIcon } = action;

const pendingTableColumnHelper = createColumnHelper<BusinessHierarchyInvitation>();

const caption = "Pending children invitations";

export const PendingChildrenTable = () => {
  const { businessId: parentBusinessId } = useParams<{ businessId: string }>();
  const {
    businessChildrenInvitations,
    loadingBusinessChildrenInvitations,
    cancelChildInvitation
  } = useBusinessChildInvitation(parentBusinessId);

  const pendingChildrenTableColumns = useMemo(() => [
    pendingTableColumnHelper.accessor("state", {
      header: () => <span>State</span>,
      cell: ({ getValue }) => (
        <div className={styles[getValue()]}>
          {getValue()}
        </div>
      )
    }),
    pendingTableColumnHelper.accessor("createdAt", {
      header: () => <span>Invitation Date</span>,
      cell: ({ getValue }) => {
        const createdAt = dateToString(new Date(getValue()), {
          ...defaultDateTimeFormatOptions,
          hour: "numeric",
          minute: "numeric"
        });

        return (
          <div className={styles.createdAt}>
            {createdAt}
          </div>
        );
      }
    }),
    pendingTableColumnHelper.accessor("updatedAt", {
      header: () => <span>Updated At</span>,
      cell: ({ getValue, row }) => {
        const updated = row.original.createdAt !== row.original.updatedAt;

        return (
          <div className={styles.updatedAt}>
            {updated ? dateToString(new Date(getValue()), {
              ...defaultDateTimeFormatOptions,
              hour: "numeric",
              minute: "numeric"
            }) : "N/A"}
          </div>
        );
      }
    }),
    pendingTableColumnHelper.accessor("id", {
      header: () => <span />,
      enableSorting: false,
      cell: ({ getValue, row }) => (
        row.original.state === "pending" ? (
          <div className={styles.menuContainer}>
            <ContextMenu
              toggleIcon={ContextMenuIcon}
              toggleButtonProps={{ title: "Toggle context menu for child invitation" }}
            >
              <ContextMenuItem
                label="Cancel"
                icon={DeleteIcon}
                onClick={() => cancelChildInvitation({ parentBusinessId, invitationId: getValue() })}
              />
            </ContextMenu>
          </div>
        ) : null
      )
    })
  ], [cancelChildInvitation, parentBusinessId]);

  const { sorting, onSortingChange } = useTrackedTableSort({ table: caption });

  const sortedBusinessChildrenInvitations = useMemo(() => (businessChildrenInvitations ?? []).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  ), [businessChildrenInvitations]);
  const pendingChildrenTable = useReactTable({
    data: sortedBusinessChildrenInvitations,
    columns: pendingChildrenTableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting
    },
    onSortingChange
  });
  const pendingChildrenRowModel = pendingChildrenTable.getRowModel();
  const pendingChildrenRows = useMemo(() => pendingChildrenRowModel.rows, [pendingChildrenRowModel]);

  return (
    <Table<BusinessHierarchyInvitation>
      caption={caption}
      loading={loadingBusinessChildrenInvitations}
      className={styles.table}
      rows={pendingChildrenRows}
      columns={pendingChildrenTable.getAllColumns()}
      headerGroups={pendingChildrenTable.getHeaderGroups()}
      headerGroupProps={{
        cellClassName: styles.tableHeader
      }}
      rowProps={{
        className: styles.tableRow,
        cellClassName: styles.tableCell
      }}
      noDataText="No pending invitations"
    />
  );
};
